import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import { IDLE, PENDING, LIST, ADD, EDIT } from "const"
import { OkrSession } from "server/models"
import { okrSessionsRoute } from "client/parts/routes"
import { StateType, hasAccess } from "client/utils"
import {
    PRM_OKR_SESSION,
    LangType,
    StoreType,

    LoadAllOkrSessions,
    SortOkrSessions,
    PrepareOkrSessionNestedIds,

    setOkrSessionList,
    setOkrSessionIds,
    setOkrSessionActive,
    setOkrSessionShowArchived,
    addModal,
} from "client/store"
import {
    Container,
    PageHeader,
    PageTitle,
    PageContent,
    Loader,
    Button,
    SliderSwitcher,
    Table,
    DateFormatterString,

    TableNestedIds,
} from "client/components"

import AccessDenied from "client/parts/accessDenied"
import Columns from "./columns"
import PopupMenu from "./popupMenu"
import ConfirmDelete from "./confirmDelete"
import {
    PageTitleWrapper,
    ScrollerWrapper,
    TimelineListWrapper,
    TimelineListTitle,
    TimelineListBody,
    Period,
    YearWrapper,
    Year,
    MonthWrapper,
    Month,
    EmptyTimeline,
    TimelineWrapper,
    Timeline,
    Today,
    Tooltip,
} from "../styled"

const monthsArray = [
    "monthShort_0", "monthShort_1", "monthShort_2", "monthShort_3", "monthShort_4", "monthShort_5", "monthShort_6",
    "monthShort_7", "monthShort_8", "monthShort_9", "monthShort_10", "monthShort_11",
]

const dayTime = 24 * 3600 * 1000 // milliseconds

const List = () => {
    const sessionList = useSelector((state: StoreType) => state.okrSession.sessionList)
    const sessionIds = useSelector((state: StoreType) => state.okrSession.sessionIds)
    const reloadList = useSelector((state: StoreType) => state.okrSession.reloadList)
    const showArchived = useSelector((state: StoreType) => state.okrSession.showArchived)

    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [yearArray, setYearArray] = useState<number[]>([])
    const [timelineDays, setTimelineDays] = useState<number>(1) // period length in days
    const [timelineStartTime, setTimelineStartTime] = useState<number>(0) // period start time to calculate offset
    const [sessionArray, setSessionArray] = useState<OkrSession[]>([]) // ordered list to display timelines

    const [loadState, setLoadState] = useState<StateType>(IDLE)

    useEffect(() => (
        () => {
            // clear popup menu object
            dispatch(setOkrSessionActive(null))
        }
    ), [])

    useEffect(() => {
        if (!reloadList) return
        setLoadState(PENDING)
        LoadAllOkrSessions(userData.accountId).then((res) => {
            dispatch(setOkrSessionList(res))
            setLoadState(IDLE)
        })
    }, [reloadList])

    useEffect(() => {
        const sessionsFiltered = SortOkrSessions(Object.values(sessionList), {
            showArchived,
            showInProgress: true,
        })
        const groupedIds = PrepareOkrSessionNestedIds(sessionsFiltered)
        dispatch(setOkrSessionIds(groupedIds))

        // get displayed session array and year array
        const currentYear = new Date().getFullYear()
        let minYear = currentYear
        let maxYear = currentYear
        const years: number[] = []
        const sessions: OkrSession[] = []

        const addNested = (nested: readonly TableNestedIds[]) => {
            nested.forEach((nestedRow) => {
                const sessionId = Number(nestedRow.id)
                const session = sessionList[sessionId]
                sessions.push(session)

                const startYear = new Date(session.dateStart).getFullYear()
                const endYear = new Date(session.dateEnd).getFullYear()
                if (startYear < minYear) minYear = startYear
                if (endYear > maxYear) maxYear = endYear

                addNested(nestedRow.nestedIds)
            })
        }
        groupedIds.forEach((group) => {
            addNested(group.nestedIds)
        })

        for (let year = minYear; year <= maxYear; year++) {
            years.push(year)
        }

        setYearArray(years)
        setSessionArray(sessions)
    }, [sessionList, showArchived])

    useEffect(() => {
        if (yearArray.length) {
            const dateStart = new Date(`${yearArray[0]}-01-01`)
            const dateEnd = new Date(`${yearArray[yearArray.length - 1]}-12-31`)

            const allDays = Math.floor((dateEnd.getTime() - dateStart.getTime()) / dayTime)

            setTimelineDays(allDays)
            setTimelineStartTime(dateStart.getTime())
        }
    }, [yearArray])

    const moveTooltip = (e: React.MouseEvent<HTMLDivElement, MouseEvent>, centerAlign = false) => {
        const target = e.currentTarget as HTMLElement
        const tooltip = target.nextSibling as HTMLElement
        const timelineBody = document.querySelector(TimelineListBody) as HTMLElement

        const targetOffset = target.getBoundingClientRect()
        const timelineBodyOffset = timelineBody.getBoundingClientRect()

        const offsetY = targetOffset.top - timelineBodyOffset.top - tooltip.offsetHeight - 8
        const offsetX = centerAlign
            ? targetOffset.left - timelineBodyOffset.left + target.offsetWidth / 2 - tooltip.offsetWidth / 2 - 1
            : e.pageX - timelineBodyOffset.left - tooltip.offsetWidth / 2

        tooltip.style.opacity = "1"
        tooltip.style.transform = `translate(${offsetX}px, ${offsetY}px)`
    }

    const hideTooltip = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        const target = e.currentTarget as HTMLElement
        const tooltip = target.nextSibling as HTMLElement
        tooltip.style.opacity = "0"
    }

    const columns = Columns()

    const listAccess = hasAccess(PRM_OKR_SESSION, LIST)
    const addAccess = hasAccess(PRM_OKR_SESSION, ADD)

    const dayPercent = 100 / timelineDays

    const today = new Date()
    const todayDaysOffset = Math.floor((today.getTime() - timelineStartTime) / dayTime)
    const todayTitle = `${lang.today}, ${DateFormatterString(new Date(), lang).date}`

    if (!listAccess) return <AccessDenied />
    return (
        <Container>
            <PageHeader>
                <PageTitle>{lang.sessions}</PageTitle>
                <PageTitleWrapper>
                    <SliderSwitcher
                        active={showArchived}
                        title={lang.showArchivedSessions}
                        reverseAlign
                        onClick={() => dispatch(setOkrSessionShowArchived(!showArchived))}
                    />
                    {
                        addAccess && (
                            <Button
                                variant="success"
                                onClick={() => dispatch(addModal({ url: `${okrSessionsRoute}/${EDIT}/0` }))}
                            >
                                {lang.add}
                            </Button>
                        )
                    }
                </PageTitleWrapper>
            </PageHeader>
            <PageContent>
                {
                    loadState === PENDING
                    && <Loader />
                }
                <ScrollerWrapper>
                    <Table
                        columns={columns}
                        dataList={sessionList}
                        dataGroupedIds={sessionIds}
                        expandedItems
                        rowSize="md"
                        showPagination={false}
                        showScroll={false}
                        showBottom={false}
                    />

                    <TimelineListWrapper>
                        <TimelineListTitle>
                            {
                                yearArray.map((year) => (
                                    <Period key={year}>
                                        <YearWrapper>
                                            <Year>{year}</Year>
                                        </YearWrapper>
                                        <MonthWrapper>
                                            {
                                                monthsArray.map((month) => (
                                                    <Month key={month}>{lang[month as keyof LangType]}</Month>
                                                ))
                                            }
                                        </MonthWrapper>
                                    </Period>
                                ))
                            }
                        </TimelineListTitle>
                        <TimelineListBody timelineWidth={yearArray.length * monthsArray.length * 60}>
                            <Today
                                leftOffset={dayPercent * todayDaysOffset}
                                onMouseEnter={(e) => moveTooltip(e, true)}
                                onMouseLeave={hideTooltip}
                            />
                            <Tooltip>{todayTitle}</Tooltip>

                            {
                                sessionArray.length === 0 && <EmptyTimeline />
                            }
                            {
                                sessionArray.map((session) => {
                                    const startTime = new Date(session.dateStart).getTime()
                                    const endTime = new Date(session.dateEnd).getTime()

                                    const sessionDays = Math.floor((endTime - startTime) / dayTime)
                                    const sessionDaysOffset = Math.floor((startTime - timelineStartTime) / dayTime)

                                    const start = DateFormatterString(session.dateStart, lang).date
                                    const end = DateFormatterString(session.dateEnd, lang).date
                                    const title = `${start} - ${end}`

                                    return (
                                        <TimelineWrapper key={session.id}>
                                            <Timeline
                                                leftOffset={dayPercent * sessionDaysOffset}
                                                length={dayPercent * sessionDays}
                                                sessionColor={session.color}
                                                onMouseMove={moveTooltip}
                                                onMouseLeave={hideTooltip}
                                            />
                                            <Tooltip>{title}</Tooltip>
                                        </TimelineWrapper>
                                    )
                                })
                            }
                        </TimelineListBody>
                    </TimelineListWrapper>
                </ScrollerWrapper>

                <PopupMenu />
                <ConfirmDelete />

            </PageContent>
        </Container>
    )
}
export default List
